import { useEffect, useRef, useState } from 'react';
import type { IGalleryCommonProps, IGalleryComponentProps, } from '@media-ui/gallery/src/components/Gallery/Gallery.types';
import { useGallery } from '@media-ui/gallery/src/components/Gallery/GalleryHooks/';
import * as T from './useShowGallery.types';
import { useInfographicGalleries } from './useInfographicGallery';
import { getItemsWithSourcePrefix } from './useShowGallery.utils';
import { useExtractedGalleries } from './useExtractedGallery';
import GalleryEventsDispatcher, { EVENT_NAMES, } from 'Src/tools/virtualGallery/galleryEventsDispatcher';
import { IArticleServerData } from 'Src/pages/ArticlePage/ArticlePage.types';
import { IGalleryItemExtended } from 'Src/store/context/types';
const useShowGallery = (galleryData: IArticleServerData['galleryData'], pageURL: IArticleServerData['href'], articleID: IArticleServerData['id']): T.IGalleryShowManager => {
    const [isMount, setIsMount] = useState<boolean>(false);
    const [initialSlide] = useState<string>();
    const commonGalleryItems = galleryData?.common ?? [];
    const initialExtractedGalleries = galleryData?.galleries ?? [];
    const infographicGalleryItems = galleryData?.infographics ?? [];
    const { current: galleryURLStore } = useRef(new Map());
    const { current: eventsDispatcher } = useRef(new GalleryEventsDispatcher({
        initialItems: commonGalleryItems,
        initialPageURL: pageURL,
    }));
    const onSlide = (slide: any): void => {
        eventsDispatcher.dispatch(EVENT_NAMES.CHANGE, {
            slideIndex: slide,
        });
    };
    const onUnmount = (): void => {
        setIsMount(false);
        eventsDispatcher.dispatch(EVENT_NAMES.CLOSE);
    };
    const onMount = (): void => {
        setIsMount(true);
    };
    const onSplash = (): void => {
        eventsDispatcher.dispatch(EVENT_NAMES.SPLASH);
    };
    const onAfterChange: IGalleryCommonProps['onAfterChange'] = (index: number, { type, slide }) => {
        if (type === 'image' && slide !== undefined) {
            onSlide(slide);
        }
        else if (type === 'lastSlide') {
            onSplash();
        }
    };
    const onGalleryChange = (data: IGalleryComponentProps): void => {
        const pageURL: string = galleryURLStore.get(data.id);
        if (pageURL) {
            eventsDispatcher.updatePageURL(pageURL);
        }
        eventsDispatcher
            .updateSlides(data.items as IGalleryItemExtended[])
            .updateIsLoadedGallery(data.id !== INITIAL_GALLERY_ID);
    };
    const commonGalleryProps = {
        commonProps: {
            onUnmount,
            onMount,
            onGalleryChange,
            onAfterChange,
        },
    };
    const { show: commonGalleryShow } = useGallery(`Gallery-${articleID}`, {
        galleryProps: {
            ...commonGalleryProps,
            items: getItemsWithSourcePrefix(commonGalleryItems),
        },
    });
    const { extractedGalleries, extractedGalleryIndicies } = useExtractedGalleries({
        initialExtractedGalleries,
        galleryProps: commonGalleryProps,
        articleID,
    });
    const { infographicGalleries } = useInfographicGalleries({
        initialInfographicItems: infographicGalleryItems,
        galleryProps: commonGalleryProps,
        articleID,
    });
    const INITIAL_GALLERY_ID = `Gallery-${articleID || 'common'}`;
    useEffect(() => {
        if (!initialSlide || !isMount) {
            return;
        }
        /**
         * Дергаем виртуальную галерею на открытие первого слайда
         * То есть, когда есть ID слайда и он уже отрендерился
         */
        onSlide(initialSlide);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialSlide, isMount]);
    const showManager = (slideID: string): T.IOverlayDispatcher | undefined => {
        let showGallery = commonGalleryShow;
        switch (true) {
            case slideID in extractedGalleryIndicies:
                showGallery =
                    extractedGalleries[String(extractedGalleryIndicies[slideID])];
                break;
            case slideID in infographicGalleries:
                showGallery = infographicGalleries[slideID];
                break;
        }
        return () => {
            return showGallery({
                initialSlideId: slideID,
            });
        };
    };
    return showManager;
};
export default useShowGallery;
